import {
  ENTITY_CLASS,
  type FacilityKey,
  FacilityKeySchema,
  type Group,
  type GroupKey,
  GroupKeySchema,
  type GroupQuery,
} from "@packages/core";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import * as v from "valibot";
import { create } from "zustand";

import { fetchData, findGroups } from "./functions";
import { QueryKey } from "./query";
import { NETWORK_SEMAPHORE } from "./semaphore";
import { TANSTACK_QUERY_CLIENT } from "./tanstack";

export async function findGroup(groupKey: GroupKey | false | null | undefined) {
  if (!groupKey) {
    return undefined;
  }
  const { output } = await fetchData({ type: "Entity::Group", input: { groupKey } });
  if (output instanceof Error) {
    throw output;
  } else {
    return output;
  }
}

function GroupQueryKey(groupKey: GroupKey | false | null | undefined) {
  return QueryKey(ENTITY_CLASS.GROUP, groupKey);
}

export function updateGroupQueryCache(
  groupKey: GroupKey | false | null | undefined,
  group?: Group | { (group: Group | null | undefined): Group | null | undefined },
  options?: Partial<{ invalidate: boolean }>,
) {
  if (groupKey) {
    const queryKey = GroupQueryKey(groupKey);
    if (group) {
      TANSTACK_QUERY_CLIENT.setQueryData(queryKey, group);
    }
    if (options?.invalidate) {
      TANSTACK_QUERY_CLIENT.invalidateQueries({ exact: true, queryKey });
    }
  }
}

export function useGroupQuery(groupKey: GroupKey | false | null | undefined) {
  return useQuery({
    queryKey: GroupQueryKey(groupKey),
    async queryFn() {
      return findGroup(groupKey);
    },

    enabled: Boolean(groupKey),
    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    staleTime: /** 4時間 */ 14_400_000,
  });
}

function GroupInfiniteQueryKey(...[filters, options]: Parameters<typeof useGroupInfiniteQuery>) {
  return QueryKey(ENTITY_CLASS.GROUP + "List", filters, options);
}

export function removeGroupInfiniteQueryCache() {
  TANSTACK_QUERY_CLIENT.invalidateQueries({ queryKey: GroupInfiniteQueryKey({}).slice(0, 1) });
}

export function useGroupInfiniteQuery(
  query: GroupQuery,
  options?: Partial<{
    /** 処理を実行する場合に`true`を設定するフラグ */
    enabled: boolean;
  }>,
) {
  return useInfiniteQuery({
    queryKey: GroupInfiniteQueryKey(query, options),
    async queryFn(context) {
      const release = await NETWORK_SEMAPHORE.acquire();
      try {
        const { groups, nextPageToken } = await findGroups({ ...query, ...context.pageParam });

        for (const group of groups) {
          updateGroupQueryCache(group.groupKey, group);
        }

        return { groups, nextPageToken };
      } finally {
        release();
      }
    },

    getNextPageParam(lastPage) {
      return lastPage.nextPageToken ? { nextPageToken: lastPage.nextPageToken } : undefined;
    },
    initialPageParam: {} as { nextPageToken?: Record<string, any> },

    enabled: options?.enabled,
    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    staleTime: /** 4時間 */ 14_400_000,
  });
}

interface SelectedGroupKeyStore {
  selectedGroupKeys: Readonly<Partial<Record<FacilityKey, ReadonlyArray<GroupKey>>>>;
  setSelectedGroupKeys(
    facilityKey: FacilityKey,
    groupKeys:
      | ReadonlyArray<GroupKey>
      | { (groupKeys: ReadonlyArray<GroupKey> | undefined): ReadonlyArray<GroupKey> | undefined },
  ): void;
}
const useSelectedGroupKeyStore = create<SelectedGroupKeyStore>(function (set) {
  let groupKeys;
  try {
    groupKeys = v.parse(
      v.record(FacilityKeySchema(), v.array(GroupKeySchema())),
      JSON.parse(window.localStorage.getItem("Reha30.SelectedGroupKeys") ?? "{}"),
    );
  } catch (error) {
    console.warn("ローカルストレージから選択されたグループキーを取得出来ません。", error);
    groupKeys = {};
  }

  return {
    selectedGroupKeys: groupKeys,
    setSelectedGroupKeys(facilityKey, groupKeys) {
      set(function (state) {
        const selectedGroupKeys = {
          ...state.selectedGroupKeys,
          [facilityKey]:
            typeof groupKeys === "function"
              ? groupKeys(state.selectedGroupKeys[facilityKey])
              : groupKeys,
        };
        try {
          window.localStorage.setItem(
            "Reha30.SelectedGroupKeys",
            JSON.stringify(selectedGroupKeys),
          );
        } catch (error) {
          console.warn("ローカルストレージに選択されたグループキーを保存出来ません。", error);
        }
        return { selectedGroupKeys };
      });
    },
  };
});

export function useSelectedGroupKeys(facilityKey: FacilityKey) {
  const selectedGroupKeys = useSelectedGroupKeyStore(function (state) {
    return state.selectedGroupKeys[facilityKey];
  });

  const setSelectedGroupKeys = useCallback(
    function (
      groupKeys:
        | ReadonlyArray<GroupKey>
        | { (groupKeys: ReadonlyArray<GroupKey> | undefined): ReadonlyArray<GroupKey> | undefined },
    ) {
      useSelectedGroupKeyStore.getState().setSelectedGroupKeys(facilityKey, groupKeys);
    },
    [facilityKey],
  );

  return useMemo(
    function () {
      return [selectedGroupKeys ?? [], setSelectedGroupKeys] as const;
    },
    [selectedGroupKeys, setSelectedGroupKeys],
  );
}
